import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.jsx';
import { isAiKeyBannerDismissed, dismissAiKeyBanner } from '../util/sessionFlags.js';

// T112 — Persistent "no Anthropic key" banner + inline equivalent for the AI
// modals. Replaces the old reactive error-toast posture (the operator only
// discovered the missing key by triggering an AI action and reading a 5s toast)
// with an upfront, always-visible signal.
//
// Key signal: `!!user?.connectors?.anthropicKey`. serializeUser() ships a MASKED
// connectors object (`••••XXXX` or ''), so this is a leak-free "key is set"
// boolean — the exact convention Dashboard/Profile/Onboarding already use.
//
// Dismiss is module-level (not localStorage): it survives route changes — the
// Layout, and so this banner, remounts on every navigation — but resets on a
// full reload. A persisted dismiss could hide a genuinely-unconfigured account
// on a fresh login; once the key is set the banner disappears for good anyway.
// T290 — the reasoning above turns on "resets on a full reload", and logout is
// not one: it calls setUser(null) and stays on the page. So the very case the
// comment set out to avoid — a dismiss hiding a genuinely-unconfigured account
// on a fresh login — is what happened when a second person signed in on the
// same tab. The flag now lives in util/sessionFlags.js, which logout clears.

export default function AiKeyBanner() {
  const { user } = useAuth();
  const [, force] = useState(0);
  const hasKey = !!user?.connectors?.anthropicKey;
  if (!user || hasKey || isAiKeyBannerDismissed()) return null;
  return (
    <div
      role="status"
      className="mb-4 flex items-start gap-3 rounded-xl border border-amber-800/60 bg-amber-950/20 p-3 text-sm text-amber-200"
    >
      <span aria-hidden="true" className="mt-0.5">
        ⚠️
      </span>
      <div className="min-w-0 flex-1">
        <p className="font-medium">AI features are off — no Anthropic API key</p>
        <p className="mt-0.5 text-xs opacity-80">
          Explanations, cover letters, tailored CVs, interview prep, company research and
          manual-job scoring all need a key.{' '}
          <Link to="/settings" className="underline decoration-dotted hover:text-amber-100">
            Add one in Settings → Connectors
          </Link>
          .
        </p>
      </div>
      <button
        type="button"
        onClick={() => {
          dismissAiKeyBanner();
          force((n) => n + 1);
        }}
        aria-label="Dismiss AI key warning"
        className="rounded-md px-1.5 text-xs opacity-60 hover:opacity-100"
      >
        ✕
      </button>
    </div>
  );
}

// Inline equivalent shown inside the AI modals in place of the "✨ Generate"
// button when no key is configured. Presentational — the modal decides when to
// render it (only when `!hasAnthropicKey`).
export function AiKeyMissingNotice({ feature = 'This feature' }) {
  return (
    <div className="rounded-lg border border-amber-700 bg-amber-950/15 p-3 text-sm text-amber-200">
      <p className="font-medium">⚠️ {feature} needs an Anthropic API key</p>
      <p className="mt-1 text-xs opacity-80">
        You haven&apos;t set one yet.{' '}
        <Link to="/settings" className="underline decoration-dotted hover:text-amber-100">
          Add it in Settings → Connectors
        </Link>{' '}
        — takes a minute, then come back and generate.
      </p>
    </div>
  );
}
